import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { switchMap, timer, takeWhile } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { LucideIconsModule } from 'src/app/shared/icons/lucide-icons.module';
import { SimulationStatusBadgeComponent } from '../simulation-status-badge/simulation-status-badge.component';
import { RedTeamHttpService } from 'src/app/services/httpServices/redTeamHttpService/red-team-http.service';
import { IRedTeamSession, IDetectedIssue } from 'src/app/models/api/red-team/red-team-session.model';
import { RedTeamSessionStatus } from 'src/app/models/api/red-team/red-team.enums';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';

const POLL_INTERVAL_MS = 5000;

interface StepConfig {
  readonly label: string;
  readonly state: 'completed' | 'active' | 'pending';
}

const AI_SERVICE_DISPLAY: Record<string, string> = {
  m365Copilot: 'Copilot for Microsoft 365',
};

interface SensitivityConfig {
  readonly label: string;
  readonly icon: string;
  readonly bgColor: string;
  readonly iconColor: string;
  readonly order: number;
}

const SENSITIVITY_CONFIG: Record<string, SensitivityConfig> = {
  RESTRICTED: {
    label: 'Restricted',
    icon: 'shield-ban',
    bgColor: '#f3e8ea',
    iconColor: '#7d1925',
    order: 0,
  },
  CRITICAL: {
    label: 'Critical',
    icon: 'shield-x',
    bgColor: '#fceaec',
    iconColor: '#b42626',
    order: 1,
  },
  HIGH: {
    label: 'High',
    icon: 'shield-alert',
    bgColor: '#fef0f0',
    iconColor: '#ef4444',
    order: 2,
  },
  MEDIUM: {
    label: 'Medium',
    icon: 'shield-minus',
    bgColor: '#fff6ec',
    iconColor: '#ffa640',
    order: 3,
  },
  LOW: {
    label: 'Low',
    icon: 'shield',
    bgColor: '#fefce8',
    iconColor: '#f9d856',
    order: 4,
  },
};

const DEFAULT_SENSITIVITY: SensitivityConfig = {
  label: '-',
  icon: 'circle-dot',
  bgColor: '#f6f6f6',
  iconColor: '#4c4a61',
  order: 99,
};

const FILE_TYPE_ICONS = new Set([
  'CSV',
  'DOCX',
  'IPYNB',
  'JPG',
  'JS',
  'JSON',
  'LOOP',
  'MD',
  'PDF',
  'PNG',
  'PPTX',
  'PY',
  'TXT',
  'XL',
  'XLSX',
  'YML',
]);

type SortField = 'fileName' | 'sensitivity';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-session-drawer',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    LabelsProviderPipe,
    LucideIconsModule,
    SimulationStatusBadgeComponent,
  ],
  templateUrl: './session-drawer.component.html',
  styleUrl: './session-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionDrawerComponent implements OnInit {
  private readonly _redTeamHttp = inject(RedTeamHttpService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _logger = inject(LoggerService);

  readonly sessionId = input.required<string>();
  readonly viewModel = input<IRedTeamSession>();

  readonly currentSession = signal<IRedTeamSession | null>(null);
  readonly expandedIssueIdx = signal<number | null>(null);
  readonly sortField = signal<SortField>('fileName');
  readonly sortDirection = signal<SortDirection>('asc');

  readonly isInProgress = computed(() => {
    const s = this.currentSession();
    if (!s) {
      return false;
    }
    return s.status === RedTeamSessionStatus.Pending || s.status === RedTeamSessionStatus.Executing;
  });

  readonly isFinished = computed(() => {
    const s = this.currentSession();
    if (!s) {
      return false;
    }
    return s.status === RedTeamSessionStatus.FinishedSuccess || s.status === RedTeamSessionStatus.FinishedFail;
  });

  readonly isFailed = computed(() => this.currentSession()?.status === RedTeamSessionStatus.FinishedFail);

  readonly issueCount = computed(() => this.currentSession()?.detectedIssues?.length ?? 0);

  readonly sortedIssues = computed(() => {
    const issues = this.currentSession()?.detectedIssues ?? [];
    const field = this.sortField();
    const dir = this.sortDirection();
    const sorted = [...issues].sort((a, b) => {
      let cmp = 0;
      if (field === 'fileName') {
        const nameA = this.getFileName(a);
        const nameB = this.getFileName(b);
        cmp = nameA.localeCompare(nameB);
      } else {
        const orderA = (SENSITIVITY_CONFIG[a.sensitivity ?? ''] ?? DEFAULT_SENSITIVITY).order;
        const orderB = (SENSITIVITY_CONFIG[b.sensitivity ?? ''] ?? DEFAULT_SENSITIVITY).order;
        cmp = orderA - orderB;
      }
      return dir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  });

  readonly steps = computed<StepConfig[]>(() => {
    const s = this.currentSession();
    const status = s?.status ?? RedTeamSessionStatus.Pending;
    const base = 'attackSimulationsPage.drawer.steps';

    if (status === RedTeamSessionStatus.Pending) {
      return [
        { label: `${base}.creatingSession`, state: 'active' },
        { label: `${base}.executingPrompts`, state: 'pending' },
        { label: `${base}.analyzingResponses`, state: 'pending' },
        { label: `${base}.detectingSensitiveData`, state: 'pending' },
      ];
    }
    if (status === RedTeamSessionStatus.Executing) {
      return [
        { label: `${base}.creatingSession`, state: 'completed' },
        { label: `${base}.executingPrompts`, state: 'active' },
        { label: `${base}.analyzingResponses`, state: 'pending' },
        { label: `${base}.detectingSensitiveData`, state: 'pending' },
      ];
    }
    return [
      { label: `${base}.creatingSession`, state: 'completed' },
      { label: `${base}.executingPrompts`, state: 'completed' },
      { label: `${base}.analyzingResponses`, state: 'completed' },
      { label: `${base}.detectingSensitiveData`, state: 'completed' },
    ];
  });

  ngOnInit(): void {
    const vm = this.viewModel();
    if (vm) {
      this.currentSession.set(vm);
    }
    this._fetchSession();
  }

  toggleSort(field: SortField): void {
    if (this.sortField() === field) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortDirection.set('asc');
    }
    this.expandedIssueIdx.set(null);
  }

  toggleIssue(idx: number): void {
    this.expandedIssueIdx.set(this.expandedIssueIdx() === idx ? null : idx);
  }

  getAiServiceDisplay(service: string | null): string {
    if (!service) {
      return '-';
    }
    return AI_SERVICE_DISPLAY[service] ?? service;
  }

  getFileName(issue: IDetectedIssue): string {
    return issue.fileName || '-';
  }

  getFileTypeIcon(issue: IDetectedIssue): string {
    const name = issue.fileName ?? '';
    const dotIdx = name.lastIndexOf('.');
    if (dotIdx < 0) {
      return '';
    }
    const ext = name.substring(dotIdx + 1).toUpperCase();
    if (FILE_TYPE_ICONS.has(ext)) {
      return `assets/images/file-types/${ext}.svg`;
    }
    return '';
  }

  getFileLocation(issue: IDetectedIssue): string {
    return issue.dataItemId ?? '-';
  }

  getShortenedUrl(issue: IDetectedIssue): string {
    const url = issue.fileUrl;
    if (!url) {
      return '-';
    }
    try {
      const parsed = new URL(url);
      const pathParts = parsed.pathname.split('/');
      const lastSegment = pathParts[pathParts.length - 1];
      const decoded = decodeURIComponent(lastSegment);
      return `${parsed.origin}/.../${decoded}`;
    } catch {
      return url;
    }
  }

  getSensitivityConfig(issue: IDetectedIssue): SensitivityConfig {
    return SENSITIVITY_CONFIG[issue.sensitivity ?? ''] ?? DEFAULT_SENSITIVITY;
  }

  getAssessmentName(session: IRedTeamSession): string {
    return session.attackName ?? '-';
  }

  onExportCsv(): void {
    this._redTeamHttp
      .exportSessionCsv(this.sessionId())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => window.open(res.fileUrl, '_blank'),
        error: (err) => this._logger.error('Failed to export CSV', err as object),
      });
  }

  formatAttackStrategy(attackName: string | null): string {
    if (!attackName) {
      return '-';
    }
    return attackName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private _fetchSession(): void {
    this._redTeamHttp
      .getSession(this.sessionId())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (s) => {
          this.currentSession.set(s);
          if (this.isInProgress()) {
            this._startPolling();
          }
        },
        error: (err) => this._logger.error('Failed to fetch session', err as object),
      });
  }

  private _startPolling(): void {
    timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this._redTeamHttp.getSession(this.sessionId())),
        takeWhile(
          (s) => s.status === RedTeamSessionStatus.Pending || s.status === RedTeamSessionStatus.Executing,
          true
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (s) => this.currentSession.set(s),
        error: (err) => this._logger.error('Failed to poll session', err as object),
      });
  }
}
